import { useEffect, useRef } from 'react'
import { IconClose } from './icons'
import {
  APP_VERSION,
  ARTEN,
  ART_LABELS,
  FASSUNGEN,
  formatiereDatum,
  type Art,
  type Fassung,
} from '@/features/changelog/fassungen'
import { merkeGesehen } from '@/features/changelog/gesehen'

/**
 * Der Änderungsverlauf der App — als Blatt über der Ansicht, aus der er gerufen wurde.
 *
 * **Warum keine eigene Ansicht:** Der Verlauf ist kein Ort der App, sondern ein Blick
 * hinein. Er wird gelesen und wieder zugemacht; niemand navigiert dorthin, um dort zu
 * sein. Eine eigene Adresse brächte einen Eintrag in der Historie, einen Reiter, der
 * auf nichts zeigt, und einen Zurück-Weg, der geklärt werden müsste — für eine Liste,
 * die man einmal überfliegt. Dieselbe Überlegung wie bei der Suche, nur ohne deren
 * Grund, trotzdem eine Adresse zu haben (teilbare Treffer).
 *
 * Wer ihn öffnet, hat die Neuerungen gesehen — auch die, die der Hinweis gar nicht
 * genannt hätte. Deshalb wird hier der Stand mitgeführt; sonst käme der Dialog beim
 * nächsten Start erneut, obwohl der Nutzer die vollständige Liste gerade gelesen hat.
 */
export function AenderungenDialog({ onSchliessen }: { onSchliessen: () => void }) {
  const ref = useRef<HTMLDialogElement>(null)
  const inhalt = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const dialog = ref.current
    dialog?.showModal()
    // Der Fokus gehört auf den Dialog selbst und nicht auf das erste bedienbare
    // Element: Die Sprachausgabe liest ihn dann von vorn, und der Fokusring steht
    // nicht als Kasten mitten im Text. Gleiches Vorgehen wie im Hinweisdialog.
    inhalt.current?.focus()
    return () => dialog?.close()
  }, [])

  useEffect(() => {
    void merkeGesehen()
  }, [])

  return (
    <dialog
      ref={ref}
      aria-label="Änderungen"
      onCancel={(event) => {
        // Erst entscheidet die App, dann verschwindet der Dialog — sonst bliebe er
        // beim nächsten Rendern als geschlossenes Element stehen.
        event.preventDefault()
        onSchliessen()
      }}
      className="m-0 h-full max-h-none w-full max-w-none bg-transparent p-0
                 backdrop:bg-[rgb(0_0_0/0.6)]"
    >
      <div
        className="flex h-full items-end justify-center px-3"
        style={{ paddingBottom: 'calc(var(--nav-hoehe) + 0.75rem)' }}
      >
        <div
          ref={inhalt}
          tabIndex={-1}
          // Höhe begrenzt und der Inhalt scrollt darin: Der Verlauf wächst mit jeder
          // Fassung, und ein Blatt, das über den Bildschirm hinausläuft, nähme dem
          // Schließen-Knopf seinen Platz.
          className="flex max-h-[80dvh] w-full max-w-2xl flex-col overflow-hidden rounded-card
                     border border-line bg-surface shadow-card focus:outline-none"
        >
          <header className="flex items-center justify-between gap-3 border-b border-line px-4 py-3">
            <h2 className="text-lg font-bold leading-tight text-ink-strong">Änderungen</h2>
            <button
              type="button"
              onClick={onSchliessen}
              aria-label="Änderungen schließen"
              className="tap -mr-2 flex shrink-0 items-center justify-center px-2 text-muted hover:text-ink"
            >
              <IconClose className="size-5" />
            </button>
          </header>

          <div className="flex-1 overflow-y-auto overscroll-contain px-4 py-4">
            <ol className="space-y-4">
              {FASSUNGEN.map((fassung) => (
                <li key={fassung.version}>
                  <FassungKarte fassung={fassung} />
                </li>
              ))}
            </ol>

            <p className="mt-5 text-xs leading-relaxed text-muted">
              Die Nummer folgt der Semantischen Versionierung: Die mittlere Stelle steigt,
              wenn Funktionen hinzukommen, die letzte bei Fehlerbehebungen und Feinschliff.
              Über Fassungen mit neuen Funktionen meldet sich die App von sich aus.
            </p>
          </div>
        </div>
      </div>
    </dialog>
  )
}

function FassungKarte({ fassung }: { fassung: Fassung }) {
  const aktuell = fassung.version === APP_VERSION

  return (
    <article className="overflow-hidden rounded-card border border-line bg-surface">
      <header className="flex items-baseline justify-between gap-3 border-b border-line px-3 py-2">
        <h3 className="flex items-baseline gap-2 font-bold leading-tight text-ink-strong">
          <span className="tabular-nums">{fassung.version}</span>
          {/* Nur an der laufenden Fassung, und als Wort statt als Farbe: Die Marke
              beantwortet die erste Frage des Lesers — welche habe ich gerade? */}
          {aktuell && (
            <span
              className="inline-flex items-center rounded-chip border border-accent px-1.5 py-px
                         text-[11px] font-semibold uppercase tracking-wider text-accent"
            >
              Installiert
            </span>
          )}
        </h3>
        <time dateTime={fassung.datum} className="shrink-0 text-xs tabular-nums text-muted">
          {formatiereDatum(fassung.datum)}
        </time>
      </header>

      <div className="space-y-3 px-3 py-2.5">
        {ARTEN.map((art) => (
          <Gruppe key={art} art={art} fassung={fassung} />
        ))}
      </div>
    </article>
  )
}

function Gruppe({ art, fassung }: { art: Art; fassung: Fassung }) {
  const zeilen = fassung.aenderungen.filter((aenderung) => aenderung.art === art)
  if (zeilen.length === 0) return null

  return (
    <section>
      <h4 className="text-xs font-semibold uppercase tracking-wider text-muted">
        {ART_LABELS[art]}
      </h4>
      <ul className="mt-1.5 space-y-1.5">
        {zeilen.map((aenderung) => (
          // Der Punkt liegt außerhalb des Textflusses, damit mehrzeilige Einträge
          // sauber unter ihrem ersten Wort weiterlaufen statt unter dem Punkt.
          <li key={aenderung.text} className="flex gap-2 text-sm leading-relaxed text-ink">
            <span aria-hidden className="mt-2 size-1.5 shrink-0 rounded-full bg-accent" />
            <span>{aenderung.text}</span>
          </li>
        ))}
      </ul>
    </section>
  )
}
